import React, { ElementType, useLayoutEffect, useRef, useState } from 'react';

interface FitTextProps {
  /** The text to display. Treated as a single line. */
  text: string;
  /** Tag to render. Defaults to `span`. */
  as?: ElementType;
  /** Tailwind / utility classes applied to the rendered tag. */
  className?: string;
  /** Smallest font size in px the text can shrink to. */
  minPx?: number;
  /** Largest font size in px the text starts from. If omitted, picks a viewport-appropriate value. */
  maxPx?: number;
  /** Optional title attribute (defaults to the rendered text so users still see the full name on hover). */
  title?: string;
}

function getResponsiveMaxPx(): number {
  if (typeof window === 'undefined') return 24;
  const w = window.innerWidth;
  if (w >= 1024) return 24; // lg → text-2xl
  if (w >= 768) return 20;  // md → text-xl
  if (w >= 640) return 18;  // sm → text-lg
  return 18;                // mobile → start at sm-ish, shrink as needed
}

/**
 * Single-line text that scales its font size down to fit its parent's width.
 *
 * Uses a hidden measure span and binary-searches the largest size in
 * `[minPx, maxPx]` whose rendered width fits the container. Reruns on container
 * resize (orientation change, sidebar toggle, etc.) via ResizeObserver.
 */
export default function FitText({
  text,
  as = 'span',
  className,
  minPx = 11,
  maxPx,
  title,
}: FitTextProps) {
  const Tag = as as ElementType;
  const containerRef = useRef<HTMLElement>(null);
  const [fontSize, setFontSize] = useState<number>(maxPx ?? 18);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let raf = 0;

    const fit = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const availableWidth = container.clientWidth;
        if (availableWidth <= 0) return;

        const cs = window.getComputedStyle(container);
        const measure = document.createElement('span');
        measure.style.position = 'absolute';
        measure.style.visibility = 'hidden';
        measure.style.whiteSpace = 'nowrap';
        measure.style.fontFamily = cs.fontFamily;
        measure.style.fontWeight = cs.fontWeight;
        measure.style.fontStyle = cs.fontStyle;
        measure.style.letterSpacing = cs.letterSpacing;
        measure.textContent = text;
        document.body.appendChild(measure);

        const hardMax = maxPx ?? getResponsiveMaxPx();
        let lo = minPx;
        let hi = Math.max(minPx, hardMax);
        let best = minPx;
        while (lo <= hi) {
          const mid = Math.floor((lo + hi) / 2);
          measure.style.fontSize = `${mid}px`;
          if (measure.offsetWidth <= availableWidth) {
            best = mid;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }

        document.body.removeChild(measure);
        setFontSize(best);
      });
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(container);
    window.addEventListener('resize', fit);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [text, minPx, maxPx]);

  return (
    <Tag
      ref={containerRef as React.MutableRefObject<any>}
      className={className}
      title={title ?? text}
      style={{
        fontSize: `${fontSize}px`,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
        display: 'block',
      }}
    >
      {text}
    </Tag>
  );
}
